import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { EmptyState } from '@/components/EmptyState';
import { HealthBadge } from '@/components/HealthBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Bot, Plus, Search, Loader2, Archive, ZapOff, Trash2, AlertTriangle, KeyRound } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { relativeTime, shortId } from '@/lib/utils';
import { FarmTypeFields } from '@/components/FarmTypeFields';
import { DEFAULT_BROWSER_PROFILE, osForProfile } from '@/lib/farm';

export default function Agents() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [showArchived, setShowArchived] = React.useState(false);
  const drawerOpen = params.get('new') === '1';

  const { data, isLoading } = useQuery({
    queryKey: ['agents', showArchived],
    queryFn: () => api.get('/agents', { params: { include_archived: showArchived } }).then(r => r.data),
    refetchInterval: 20000,
  });

  const { data: bwData } = useQuery({
    queryKey: ['org-bandwidth'],
    queryFn: () => api.get('/agents/usage/org-bandwidth').then(r => r.data),
    refetchInterval: 30000,
  });

  const { data: credentialsData } = useQuery({ queryKey: ['credentials'], queryFn: () => api.get('/credentials').then(r => r.data) });
  const { data: identitiesData } = useQuery({ queryKey: ['identities', 'agent-create'], queryFn: () => api.get('/identities', { params: { limit: 500 } }).then(r => r.data) });
  const { data: proxiesData } = useQuery({ queryKey: ['proxies'], queryFn: () => api.get('/proxies').then(r => r.data) });
  const { data: templatesData } = useQuery({ queryKey: ['templates'], queryFn: () => api.get('/templates').then(r => r.data) });

  const killAllMut = useMutation({
    mutationFn: () => api.post('/agents/kill-all').then(r => r.data),
    onSuccess: (d) => { toast.success(`Paused ${d.paused_agents} agents`, { description: `Cancelled ${d.cancelled_workflows} workflows` }); qc.invalidateQueries({ queryKey: ['agents'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Kill-all failed'),
  });

  const hardDeleteMut = useMutation({
    mutationFn: (id) => api.delete(`/agents/${id}/forever`),
    onSuccess: () => { toast.success('Agent permanently deleted'); qc.invalidateQueries({ queryKey: ['agents'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Delete failed'),
  });

  const filtered = React.useMemo(() => {
    let items = data?.items || [];
    if (statusFilter !== 'all') items = items.filter((a) => a.status === statusFilter);
    if (search) items = items.filter((a) => (a.name + ' ' + (a.mission || '')).toLowerCase().includes(search.toLowerCase()));
    return items;
  }, [data, statusFilter, search]);

  const openDrawer = () => setParams((p) => { p.set('new', '1'); return p; });
  const closeDrawer = () => setParams((p) => { p.delete('new'); p.delete('template'); return p; });

  return (
    <div className="space-y-6" data-testid="agents-page">
      <PageHeader
        eyebrow="Fleet"
        title="Agents"
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span>Long-running autonomous workers.</span>
            {bwData && (
              <span className="inline-flex items-center gap-1 rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--text-2))]" data-testid="header-bandwidth">
                proxy · MTD {bwData.mtd_mb} MB · 24h {bwData.last_24h_mb} MB
              </span>
            )}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="secondary" size="sm" data-testid="kill-all-button" disabled={killAllMut.isPending}>
                  <ZapOff className="mr-1.5 h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                  Stop all
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[hsl(var(--warning))]" /> Stop every active agent?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Pauses every non-archived agent and cancels any running Temporal workflows.
                    Use this when proxy bandwidth spikes unexpectedly. You can resume agents individually afterwards.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="confirm-dialog-cancel-button">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => killAllMut.mutate()} data-testid="confirm-dialog-confirm-button">Stop all</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button onClick={openDrawer} data-testid="agents-new-button">
              <Plus className="mr-2 h-4 w-4" /> New agent
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-[hsl(var(--text-3))]" />
          <Input
            placeholder="Search agents by name or mission…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="agents-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="agents-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="idle">Idle</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="errored">Errored</SelectItem>
            {showArchived && <SelectItem value="archived">Archived</SelectItem>}
          </SelectContent>
        </Select>
        <div className="inline-flex items-center gap-2 rounded-md border border-white/5 bg-[hsl(var(--surface-1))] px-2.5 py-1.5">
          <Archive className="h-3.5 w-3.5 text-[hsl(var(--text-3))]" />
          <Label htmlFor="show-archived-toggle" className="text-xs text-[hsl(var(--text-2))] cursor-pointer">
            Show archived
            {data?.summary?.archived > 0 && (
              <span className="ml-1 text-[hsl(var(--text-3))]">({data.summary.archived})</span>
            )}
          </Label>
          <Switch id="show-archived-toggle" checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-toggle" />
        </div>
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading agents…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Bot}
            title="No agents match your filter"
            description="Adjust filters or create a new agent to get started."
            action={<Button size="sm" onClick={openDrawer} data-testid="empty-new-agent-button">Create agent</Button>}
          />
        ) : (
          <Table data-testid="agents-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[26%]">Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Health</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Identity</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Bandwidth MTD</TableHead>
                <TableHead>Last run</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((a) => {
                const archived = a.status === 'archived';
                return (
                <TableRow
                  key={a.id}
                  className={`cursor-pointer ${archived ? 'opacity-50' : ''}`}
                  onClick={() => nav(`/agents/${a.id}`)}
                  data-testid={`agents-table-row-${a.id}`}
                >
                  <TableCell>
                    <div className={`font-medium ${archived ? 'line-through text-[hsl(var(--text-3))]' : ''}`}>{a.name}</div>
                    <div className="mt-0.5 font-mono text-xs text-[hsl(var(--text-3))] flex items-center gap-1">
                      {archived && <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1 py-0.5 text-[9px] uppercase tracking-wider">archived</span>}
                      <span>{shortId(a.id)} · {a.platform}</span>
                    </div>
                  </TableCell>
                  <TableCell><StatusDot state={a.status} /></TableCell>
                  <TableCell>{archived ? <span className="text-xs text-[hsl(var(--text-3))]">—</span> : <AgentHealthCell agentId={a.id} />}</TableCell>
                  <TableCell><span className="font-mono text-xs text-[hsl(var(--text-2))]">{a.llm_model}</span></TableCell>
                  <TableCell>
                    <span className="font-mono text-[10px] text-[hsl(var(--text-3))]" data-testid={`agent-identity-${a.id}`}>
                      {a.identity_id ? a.identity_id.slice(0, 8) : '—'}
                    </span>
                  </TableCell>
                  <TableCell><span className="tabular text-sm">{a.total_actions}</span></TableCell>
                  <TableCell><span className="tabular text-sm">{(a.risk_score || 0).toFixed(2)}</span></TableCell>
                  <TableCell>{archived ? <span className="text-xs text-[hsl(var(--text-3))]">—</span> : <AgentBandwidthCell agentId={a.id} budget={a.monthly_mb_budget} />}</TableCell>
                  <TableCell><span className="text-xs text-[hsl(var(--text-3))]">{a.last_run_at ? relativeTime(a.last_run_at) : 'Never'}</span></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {archived && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="rounded p-1.5 text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--danger))] transition-colors" aria-label="Delete forever" data-testid={`agent-hard-delete-${a.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {a.name} forever?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes the agent, its tasks and events. Cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => hardDeleteMut.mutate(a.id)}>Delete forever</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <CreateAgentDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        templates={templatesData?.items || []}
        credentials={credentialsData?.items || []}
        identities={(identitiesData?.items || []).filter((i) => i.status !== 'archived')}
        proxies={proxiesData?.items || []}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['agents'] }); closeDrawer(); }}
      />
    </div>
  );
}

function CreateAgentDrawer({ open, onClose, templates, credentials, identities, proxies, onCreated }) {
  const [params] = useSearchParams();
  const apiErrorMessage = (e, fallback = 'Request failed') => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string' && d.trim()) return d;
    if (d && typeof d === 'object') {
      if (typeof d.message === 'string' && d.message.trim()) return d.message;
      if (typeof d.code === 'string' && d.code.trim()) return d.code;
      try {
        return JSON.stringify(d);
      } catch {
        return fallback;
      }
    }
    if (typeof e?.message === 'string' && e.message.trim()) return e.message;
    return fallback;
  };

  const [form, setForm] = React.useState({
    name: '', mission: '', platform: 'reddit',
    credential_id: 'none', identity_id: 'none', proxy_id: 'none',
    llm_model: 'google/gemini-2.5-flash',
    stealth_level: 'aggressive', timezone: 'America/New_York',
    active_hours_start: 8, active_hours_end: 23,
    runtime: 'browser',
    tls_profile: DEFAULT_BROWSER_PROFILE,
  });
  const [template, setTemplate] = React.useState('custom');
  const [variableValues, setVariableValues] = React.useState({});
  const selectedTemplate = React.useMemo(
    () => (template === 'custom' ? null : templates.find((x) => x.slug === template) || null),
    [template, templates],
  );

  React.useEffect(() => {
    if (!open) {
      setForm({
        name: '', mission: '', platform: 'reddit',
        credential_id: 'none', identity_id: 'none', proxy_id: 'none',
        llm_model: 'google/gemini-2.5-flash',
        stealth_level: 'aggressive', timezone: 'America/New_York',
        active_hours_start: 8, active_hours_end: 23,
        runtime: 'browser',
        tls_profile: DEFAULT_BROWSER_PROFILE,
      });
      setTemplate('custom');
      setVariableValues({});
    }
  }, [open]);

  const mut = useMutation({
    mutationFn: async (payload) => {
      if (template !== 'custom') {
        const r = await api.post(`/templates/${template}/instantiate`, payload);
        return r.data?.agent || r.data;
      }
      return api.post('/agents', payload).then((r) => r.data);
    },
    onSuccess: () => { toast.success('Agent created'); onCreated(); },
    onError: (e) => toast.error(apiErrorMessage(e, 'Create failed')),
  });

  const applyTemplate = React.useCallback((slug) => {
    setTemplate(slug);
    if (slug === 'custom') {
      setVariableValues({});
      return;
    }
    const t = templates.find((x) => x.slug === slug);
    if (!t) return;
    setForm((f) => ({
      ...f,
      name: f.name || t.name,
      mission: t.mission,
      platform: ['reddit', 'linkedin', 'x'].includes(t.category) ? t.category : f.platform,
      // Template only sets default Farm Type; browser profile is chosen here.
      runtime: t.runtime || 'browser',
      tls_profile: f.tls_profile || DEFAULT_BROWSER_PROFILE,
    }));
    const defaults = {};
    (t.variables || []).forEach((v) => {
      if (v?.key) defaults[v.key] = v.default ?? (v.type === 'string_list' ? '' : '');
    });
    Object.entries(t.config || {}).forEach(([k, v]) => {
      if (defaults[k] === undefined || defaults[k] === '') {
        defaults[k] = Array.isArray(v) ? v.join(', ') : (v ?? '');
      }
    });
    setVariableValues(defaults);
  }, [templates]);

  React.useEffect(() => {
    if (!open || !templates?.length) return;
    const slug = params.get('template');
    if (slug && templates.some((t) => t.slug === slug)) {
      applyTemplate(slug);
    }
  }, [open, templates, params, applyTemplate]);

  const farmPayload = () => {
    const runtime = form.runtime === 'mobile' ? 'mobile' : 'browser';
    if (runtime === 'mobile') {
      return { runtime, tls_profile: null, browser_os: null };
    }
    const tls = form.tls_profile || DEFAULT_BROWSER_PROFILE;
    return { runtime, tls_profile: tls, browser_os: osForProfile(tls) };
  };

  const submit = () => {
    if (!form.name || !form.mission) { toast.error('Name and mission are required'); return; }
    const farm = farmPayload();
    if (template !== 'custom') {
      for (const v of (selectedTemplate?.variables || [])) {
        if (v.required && !String(variableValues[v.key] ?? '').trim()) {
          toast.error(`Missing required field: ${v.label || v.key}`);
          return;
        }
      }
      mut.mutate({
        name: form.name,
        mission: form.mission,
        ...farm,
        credential_id: form.credential_id === 'none' ? null : form.credential_id,
        identity_id: form.identity_id === 'none' ? null : form.identity_id,
        proxy_id: form.proxy_id === 'none' ? null : form.proxy_id,
        llm_model: form.llm_model,
        stealth_level: form.stealth_level,
        timezone: form.timezone,
        active_hours_start: form.active_hours_start,
        active_hours_end: form.active_hours_end,
        variables: variableValues,
        auto_bind_proxy: true,
      });
      return;
    }
    mut.mutate({
      name: form.name,
      mission: form.mission,
      platform: form.platform,
      ...farm,
      credential_id: form.credential_id === 'none' ? null : form.credential_id,
      identity_id: form.identity_id === 'none' ? null : form.identity_id,
      proxy_id: form.proxy_id === 'none' ? null : form.proxy_id,
      llm_model: form.llm_model,
      stealth_level: form.stealth_level,
      timezone: form.timezone,
      active_hours_start: form.active_hours_start,
      active_hours_end: form.active_hours_end,
    });
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-[560px] flex flex-col" data-testid="create-agent-drawer">
        <SheetHeader>
          <SheetTitle>Create agent</SheetTitle>
          <SheetDescription>Spin up a new autonomous worker. You can edit its mission later.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          <div className="space-y-1.5">
            <Label>Template</Label>
            <Select value={template} onValueChange={applyTemplate}>
              <SelectTrigger data-testid="agent-template-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom mission</SelectItem>
                {templates.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input placeholder="reddit-karma-op-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Mission</Label>
            <Textarea rows={6} placeholder="Describe what the agent should do, over what horizon, and any guardrails." value={form.mission} onChange={(e) => setForm({ ...form, mission: e.target.value })} data-testid="agent-mission-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Platform</Label>
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
              <SelectTrigger data-testid="agent-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="reddit">Reddit</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="x">X (Twitter)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <FarmTypeFields
            runtime={form.runtime}
            tlsProfile={form.tls_profile}
            onRuntimeChange={(v) => setForm({
              ...form,
              runtime: v,
              tls_profile: v === 'mobile' ? form.tls_profile : (form.tls_profile || DEFAULT_BROWSER_PROFILE),
            })}
            onTlsProfileChange={(v) => setForm({ ...form, tls_profile: v })}
            showProfile
            runtimeTestId="agent-farm-type-select"
            profileTestId="agent-browser-profile-select"
          />
          {selectedTemplate && (
            <p className="text-[10px] text-[hsl(var(--text-3))]" data-testid="template-farm-hint">
              Template default Farm Type: {(selectedTemplate.runtime || 'browser') === 'mobile' ? 'Mobile' : 'Browser'} — you can override it here.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Stealth</Label>
              <Select value={form.stealth_level} onValueChange={(v) => setForm({ ...form, stealth_level: v })}>
                <SelectTrigger data-testid="agent-stealth-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="aggressive">Aggressive</SelectItem>
                  <SelectItem value="paranoid">Paranoid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Credential</Label>
              <Select value={form.credential_id} onValueChange={(v) => setForm({ ...form, credential_id: v })}>
                <SelectTrigger data-testid="agent-credential-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {credentials.map((c) => <SelectItem key={c.id} value={c.id}>{c.label} · {c.platform}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Identity</Label>
              <Select value={form.identity_id} onValueChange={(v) => setForm({ ...form, identity_id: v })}>
                <SelectTrigger data-testid="agent-identity-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {identities.map((i) => (
                    <SelectItem key={i.id} value={i.id} data-testid={`agent-identity-option-${i.id}`}>
                      {(i.email || i.username || i.id.slice(0, 8))} · {i.platform} · {i.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1 text-[10px] text-[hsl(var(--text-3))]" data-testid="agent-identity-hint">
                <KeyRound className="h-3 w-3" /> Non-archived identities can seed browser sessions.
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Proxy</Label>
              <Select value={form.proxy_id} onValueChange={(v) => setForm({ ...form, proxy_id: v })}>
                <SelectTrigger data-testid="agent-proxy-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {proxies.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} · {p.country.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedTemplate && ((selectedTemplate.variables || []).length > 0 || (selectedTemplate.mcp_servers || []).length > 0 || selectedTemplate.playbook_md || selectedTemplate.skill_md) && (
            <div className="space-y-3 rounded-md border border-white/10 bg-[hsl(var(--surface-2))] p-3">
              <div className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--text-3))]">Agentic config</div>
              <div className="flex flex-wrap gap-2 text-[10px] text-[hsl(var(--text-3))]">
                {selectedTemplate.playbook_md ? <span className="rounded border border-white/10 px-1.5 py-0.5">Playbook</span> : null}
                {selectedTemplate.skill_md ? <span className="rounded border border-white/10 px-1.5 py-0.5">Skill</span> : null}
                {(selectedTemplate.mcp_servers || []).length > 0 ? (
                  <span className="rounded border border-white/10 px-1.5 py-0.5">
                    MCP: {(selectedTemplate.mcp_servers || []).map((m) => m.id).filter(Boolean).join(', ')}
                  </span>
                ) : null}
              </div>
              {(selectedTemplate.variables || []).map((v) => (
                <div key={v.key} className="space-y-1.5">
                  <Label>{v.label || v.key}{v.required ? ' *' : ''}</Label>
                  {v.type === 'text' ? (
                    <Textarea
                      rows={3}
                      placeholder={v.placeholder || ''}
                      value={variableValues[v.key] ?? ''}
                      onChange={(e) => setVariableValues({ ...variableValues, [v.key]: e.target.value })}
                    />
                  ) : (
                    <Input
                      placeholder={v.placeholder || (v.type === 'string_list' ? 'comma,separated,values' : '')}
                      value={variableValues[v.key] ?? ''}
                      onChange={(e) => setVariableValues({ ...variableValues, [v.key]: e.target.value })}
                    />
                  )}
                  {v.help ? <p className="text-[10px] text-[hsl(var(--text-3))]">{v.help}</p> : null}
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Model</Label>
              <Select value={form.llm_model} onValueChange={(v) => setForm({ ...form, llm_model: v })}>
                <SelectTrigger data-testid="agent-model-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="google/gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                  <SelectItem value="anthropic/claude-sonnet-4.5">Claude Sonnet 4.5</SelectItem>
                  <SelectItem value="openai/gpt-4o-mini">GPT-4o mini</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Timezone</Label>
              <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="agent-timezone-input" />
            </div>
          </div>
        </div>
        <SheetFooter className="border-t border-white/5 pt-4">
          <Button variant="secondary" onClick={onClose} data-testid="drawer-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={mut.isPending} data-testid="drawer-submit-button">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create agent
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function AgentHealthCell({ agentId }) {
  const { data } = useQuery({
    queryKey: ['health', agentId],
    queryFn: () => api.get(`/agents/${agentId}/health-score`).then(r => r.data),
    staleTime: 60_000,
  });
  if (!data) return <span className="text-xs text-[hsl(var(--text-3))]">—</span>;
  return <HealthBadge health={data} size="sm" showLabel={false} />;
}


/** Per-agent bandwidth meter — mini progress bar when budget is set,
    plain MB figure otherwise. Colour transitions green → amber → red as
    the month-to-date usage approaches the cap. */
function AgentBandwidthCell({ agentId, budget }) {
  const { data } = useQuery({
    queryKey: ['bandwidth', agentId],
    queryFn: () => api.get(`/agents/${agentId}/bandwidth-usage`).then(r => r.data),
    staleTime: 60_000,
  });
  if (!data) return <span className="text-xs text-[hsl(var(--text-3))]">—</span>;
  const mb = data.mtd_mb || 0;
  if (!budget) {
    return (
      <div className="text-xs">
        <div className="tabular text-[hsl(var(--text-2))]">{mb.toFixed(1)} MB</div>
        <div className="text-[10px] text-[hsl(var(--text-3))]">no cap</div>
      </div>
    );
  }
  const pct = Math.min(100, (mb / budget) * 100);
  const hue = pct >= 90 ? 'bg-[hsl(var(--danger))]' : pct >= 70 ? 'bg-[hsl(var(--warning))]' : 'bg-[hsl(var(--success))]';
  return (
    <div className="min-w-[110px]" data-testid={`agent-bandwidth-${agentId}`}>
      <div className="flex items-baseline justify-between text-[10px]">
        <span className="tabular font-medium">{mb.toFixed(1)} MB</span>
        <span className="tabular text-[hsl(var(--text-3))]">/ {budget}</span>
      </div>
      <div className="mt-1 h-1 rounded-full overflow-hidden bg-[hsl(var(--surface-3))]">
        <div className={`h-full ${hue} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

